import type { SimulTour } from '@lopettia/simul';

import salesRepresentativeTour from './sales-representative.tour';
import salesManagerTour from './sales-manager.tour';
import salesExecutiveTour from './sales-executive.tour';
import tenantAdminTour from './tenant-admin.tour';
import agentConfiguratorTour from './agent-configurator.tour';
import financeTour from './finance.tour';
import preSalesTour from './pre-sales.tour';
import marketingTour from './marketing.tour';
import customerSuccessTour from './customer-success.tour';
import revopsTour from './revops.tour';
import platformAdminTour from './platform-admin.tour';
import agencyOwnerTour from './agency-owner.tour';

/**
 * Registry mapping a Kairos role name to its guided tour + i18n label
 * key. The UserMenu reads this to decide which roles in the user's
 * grants surface a "Start guided tour" link (and to label the menu
 * entry when the user has multiple roles).
 *
 * Role names match the `users.role` / `roles.name` enum used by the
 * backend; they line up with the persona slugs under docs/manuals/ so
 * each tour can be cross-referenced to the long-form documentation.
 */
export interface TourRegistryEntry {
  /** The Simul tour module — passed to `startTour(...)`. */
  tour: SimulTour;
  /** i18n key for the "Start tour for <role>" menu label. */
  labelKey: string;
  /** Persona slug (matches docs/manuals/<NN>-<slug>.md). */
  persona: string;
}

export const TOUR_REGISTRY: Record<string, TourRegistryEntry> = {
  SALES_REPRESENTATIVE: {
    tour: salesRepresentativeTour,
    labelKey: 'userMenu.tour.sales-representative',
    persona: 'sales-representative',
  },
  SALES_MANAGER: {
    tour: salesManagerTour,
    labelKey: 'userMenu.tour.sales-manager',
    persona: 'sales-manager',
  },
  SALES_EXECUTIVE: {
    tour: salesExecutiveTour,
    labelKey: 'userMenu.tour.sales-executive',
    persona: 'sales-executive',
  },
  TENANT_ADMIN: {
    tour: tenantAdminTour,
    labelKey: 'userMenu.tour.tenant-admin',
    persona: 'tenant-admin',
  },
  AGENT_CONFIGURATOR: {
    tour: agentConfiguratorTour,
    labelKey: 'userMenu.tour.agent-configurator',
    persona: 'agent-configurator',
  },
  FINANCE: {
    tour: financeTour,
    labelKey: 'userMenu.tour.finance',
    persona: 'finance',
  },
  PRE_SALES: {
    tour: preSalesTour,
    labelKey: 'userMenu.tour.pre-sales',
    persona: 'pre-sales',
  },
  MARKETING: {
    tour: marketingTour,
    labelKey: 'userMenu.tour.marketing',
    persona: 'marketing',
  },
  CUSTOMER_SUCCESS: {
    tour: customerSuccessTour,
    labelKey: 'userMenu.tour.customer-success',
    persona: 'customer-success',
  },
  REVOPS: {
    tour: revopsTour,
    labelKey: 'userMenu.tour.revops',
    persona: 'revops',
  },
  PLATFORM_ADMIN: {
    tour: platformAdminTour,
    labelKey: 'userMenu.tour.platform-admin',
    persona: 'platform-admin',
  },
  AGENCY_OWNER: {
    tour: agencyOwnerTour,
    labelKey: 'userMenu.tour.agency-owner',
    persona: 'agency-owner',
  },
};

/**
 * Returns the subset of roles the current user holds that also have a
 * registered guided tour. Used by UserMenu to decide whether to show a
 * single "Start guided tour" link, a "Start tour" submenu, or nothing.
 */
export function toursForRoles(roles: string[]): Array<{ role: string; entry: TourRegistryEntry }> {
  return roles
    .filter((r) => TOUR_REGISTRY[r])
    .map((r) => ({ role: r, entry: TOUR_REGISTRY[r] }));
}
